import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DriveEtaIcon from "@mui/icons-material/DriveEta";

const defaultLeftSectionOrder = [
  'contact', 'aboutMe', 'skills', 'education'
];

const defaultRightSectionOrder = [
  'experience', 'languages', 'certifications', 'accomplishments',
  'volunteering', 'interests', 'websites', 'additionalInfo',
  'references', 'softwareSkills'
];

const overflowFix = {
  overflow: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
  maxWidth: '100%',
};

const Resume9 = ({
  color = "#8b4b2b",
  font = "Arial",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 10,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  theme,
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true
}) => {
  const primaryColor = theme?.primary || color;

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [currentPage, setCurrentPage] = useState(0);

  // ── Resume30-style: separate pages arrays for sidebar and right content ──
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);

  // ── Resume30-style: three refs (main for old compat, plus sidebar + right) ──
  const mainContentRef = useRef(null);       // kept for backward compatibility
  const sidebarContentRef = useRef(null);    // measures left sidebar sections
  const rightContentRef = useRef(null);      // measures right content sections

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    ...overflowFix,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles,
  });

  const defaultData = {
    firstName: "Laila",
    lastName: "Muhammad",
    currentPosition: "Content Creator",
    phone: "+123-456-7890",
    email: "hello@email.com",
    city: "123 Anywhere St, City",
    website: "www.reallygreatsite.com",
    linkedIn: "",
    drivingLicence: "",
    profile: "A creative writer/designer with a knack for crafting visually appealing and impactful designs.",
  };

  const data = { ...defaultData, ...formData };

  const pageSizeMap = {
    A4: { width: "210mm", height: "297mm" },
    Letter: { width: "216mm", height: "279mm" },
    Legal: { width: "216mm", height: "356mm" },
    A3: { width: "297mm", height: "420mm" },
    Executive: { width: "184mm", height: "267mm" },
  };

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ─── Drag & Drop ──────────────────────────────────────────────────────────
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };
  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };
  const handleDragOver = (e, index, side) => { e.preventDefault(); setDragOverIndex(index); setDragOverSide(side); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOverIndex(null); setDragOverSide(null); };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const d = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (d.side === 'left' && d.index !== targetIndex) {
      const arr = [...leftSectionOrder];
      const [m] = arr.splice(d.index, 1);
      arr.splice(targetIndex, 0, m);
      setLeftSectionOrder(arr);
    } else if (d.side === 'right') {
      const l = [...leftSectionOrder]; const r = [...rightSectionOrder];
      r.splice(d.index, 1); l.splice(targetIndex, 0, d.sectionId);
      setLeftSectionOrder(l); setRightSectionOrder(r);
    }
    setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const d = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (d.side === 'right' && d.index !== targetIndex) {
      const arr = [...rightSectionOrder];
      const [m] = arr.splice(d.index, 1);
      arr.splice(targetIndex, 0, m);
      setRightSectionOrder(arr);
    } else if (d.side === 'left') {
      const l = [...leftSectionOrder]; const r = [...rightSectionOrder];
      l.splice(d.index, 1); r.splice(targetIndex, 0, d.sectionId);
      setLeftSectionOrder(l); setRightSectionOrder(r);
    }
    setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => { setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity = '1'; };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => {
    const ids = Object.keys(customSections).map(n => `custom_${n}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...ids]);
  };
  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    const ids = Object.keys(customSections).map(n => `custom_${n}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...ids]);
  };
  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact', 'aboutMe': 'About Me', 'skills': 'Skills',
      'education': 'Education', 'experience': 'Experience', 'languages': 'Languages',
      'certifications': 'Certifications', 'accomplishments': 'Accomplishments',
      'volunteering': 'Volunteering', 'interests': 'Interests',
      'websites': 'Websites/Profiles', 'additionalInfo': 'Additional Information',
      'references': 'References', 'softwareSkills': 'Software Skills'
    };
    if (sectionId.startsWith('custom_')) {
      const n = sectionId.replace('custom_', '').replace(/_/g, ' ');
      return n.charAt(0).toUpperCase() + n.slice(1);
    }
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>({sections.length} sections)</Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper key={sectionId} draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease' }}>
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder}
        variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  // ─── localStorage Load ────────────────────────────────────────────────────
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
    setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
    setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
    setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
    setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
    setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
    setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
    setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
    setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};
    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        try {
          const raw = localStorage.getItem(`custom_${userId}_${sectionName}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            const validItems = (parsed.items || []).filter(item => item && item.trim() !== '');
            if (validItems.length > 0) sectionsData[sectionName] = validItems;
          }
        } catch (e) { console.error(e); }
      });
    }
    setCustomSections(sectionsData);

    const savedLeft = localStorage.getItem("resume9LeftSectionOrder");
    if (savedLeft) {
      setLeftSectionOrder(JSON.parse(savedLeft).filter(s => !s.startsWith('custom_')));
    }

    const savedRight = localStorage.getItem("resume9RightSectionOrder");
    if (savedRight) {
      const parsed = JSON.parse(savedRight).filter(s => !s.startsWith('custom_'));
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      setRightSectionOrder([...parsed, ...customIds]);
    } else {
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    if (Object.keys(customSections).length > 0) {
      const customIds = Object.keys(customSections).map(n => `custom_${n}`);
      const filtered = rightSectionOrder.filter(s => !s.startsWith('custom_'));
      const newRight = [...filtered, ...customIds];
      if (JSON.stringify(newRight) !== JSON.stringify(rightSectionOrder)) {
        setRightSectionOrder(newRight);
        localStorage.setItem("resume9RightSectionOrder", JSON.stringify(newRight));
      }
      const filteredLeft = leftSectionOrder.filter(s => !s.startsWith('custom_'));
      if (JSON.stringify(filteredLeft) !== JSON.stringify(leftSectionOrder)) {
        setLeftSectionOrder(filteredLeft);
        localStorage.setItem("resume9LeftSectionOrder", JSON.stringify(filteredLeft));
      }
    } else {
      const filteredRight = rightSectionOrder.filter(s => !s.startsWith('custom_'));
      if (filteredRight.length !== rightSectionOrder.length) {
        setRightSectionOrder(filteredRight);
        localStorage.setItem("resume9RightSectionOrder", JSON.stringify(filteredRight));
      }
      const filteredLeft = leftSectionOrder.filter(s => !s.startsWith('custom_'));
      if (filteredLeft.length !== leftSectionOrder.length) {
        setLeftSectionOrder(filteredLeft);
        localStorage.setItem("resume9LeftSectionOrder", JSON.stringify(filteredLeft));
      }
    }
  }, [customSections]);

  useEffect(() => {
    if (leftSectionOrder.length > 0) localStorage.setItem("resume9LeftSectionOrder", JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) localStorage.setItem("resume9RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  // ─── Section Heading & Divider Styles (both sides) ────────────────────────
  const sectionHeadingSx = {
    fontWeight: "bold",
    mb: 1,
    fontSize: headingSize,
    fontFamily: font,
    fontStyle: fontStyle,
    lineHeight: 1.3,
    ...overflowFix,
  };

  const dividerSx = {
    mb: 1,
    borderWidth: `${lineWeight}px`,
    backgroundColor: primaryColor,
  };

  // ─── Right Sections Renderer ──────────────────────────────────────────────
  // className: "resume-section" for right, "sidebar-section" for left measurement
  const renderRightSections = (filterIds = null) => {
    const sectionStyle = { ...overflowFix, marginBottom: `${sectionSpacing}px` };
    const itemMb = `${paragraphSpacing / 8}px`;

    return rightSectionOrder.map(sectionId => {
      if (filterIds !== null && !filterIds.includes(sectionId)) return null;

      switch (sectionId) {

        case 'experience':
          return (
            <div key="experience" data-section-id="experience" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Experience</Typography>
              <Divider sx={dividerSx} />
              {workExperiences && workExperiences.length > 0 ? (
                workExperiences.map((work, i) => (
                  <Box key={i} sx={{ mb: itemMb, ...overflowFix }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, ...overflowFix }}>
                      <Typography variant="subtitle2"
                        sx={{ fontWeight: "bold", fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, flex: '1 1 auto', minWidth: 0, ...overflowFix }}>
                        {work.jobTitle}
                      </Typography>
                      <Typography variant="caption"
                        sx={{ fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {formatDate(work.startMonth, work.startYear)} -{" "}
                        {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                      </Typography>
                    </Box>
                    {work.description && (
                      <Box sx={{
                        mt: 1, ...overflowFix,
                        "& strong": { fontWeight: "bold", fontFamily: font, fontSize: fontSize, fontStyle: fontStyle },
                        "& em": { fontStyle: "italic", fontFamily: font, fontSize: fontSize },
                        "& u": { textDecoration: "underline", fontFamily: font, fontSize: fontSize, fontStyle: fontStyle },
                        "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, ...overflowFix },
                        ...globalFontStyle,
                      }}
                        dangerouslySetInnerHTML={{
                          __html: work.description
                            .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font};font-size:${fontSize};font-style:${fontStyle};word-break:break-word;">$1</strong>`)
                            .replace(/\*(.*?)\*/g, `<em style="font-family:${font};font-size:${fontSize};word-break:break-word;">$1</em>`)
                            .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font};font-size:${fontSize};font-style:${fontStyle};word-break:break-word;">$1</u>`)
                            .replace(/\n/g, "<br/>"),
                        }}
                      />
                    )}
                  </Box>
                ))
              ) : (
                <Box sx={{ mb: itemMb, ...overflowFix }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle }}>
                    Owner & Manager of Content Agency
                  </Typography>
                  <Typography variant="caption" sx={getTextStyle()}>2020 - 2016</Typography>
                  <Typography variant="body2" sx={{ mt: 1, textIndent: `${paragraphIndent}px`, fontFamily: font, fontStyle: fontStyle, ...overflowFix }}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                  </Typography>
                </Box>
              )}
            </div>
          );

        case 'languages':
          return savedLanguages.length > 0 ? (
            <div key="languages" data-section-id="languages" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Languages</Typography>
              <Divider sx={dividerSx} />
              {savedLanguages.map((lang, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle }}>
                  {lang.name} {lang.level ? `– ${lang.level}` : ""}
                </Typography>
              ))}
            </div>
          ) : null;

        case 'certifications':
          return savedCertifications.length > 0 ? (
            <div key="certifications" data-section-id="certifications" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Certifications</Typography>
              <Divider sx={dividerSx} />
              {savedCertifications.map((cert, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle }}>
                  {cert.name} – {cert.provider} ({cert.year})
                </Typography>
              ))}
            </div>
          ) : null;

        case 'accomplishments':
          return savedAccomplishments.length > 0 ? (
            <div key="accomplishments" data-section-id="accomplishments" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Accomplishments</Typography>
              <Divider sx={dividerSx} />
              {savedAccomplishments.map((acc, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle, whiteSpace: "pre-line" }}>{acc}</Typography>
              ))}
            </div>
          ) : null;

        case 'volunteering':
          return savedVolunteering.length > 0 ? (
            <div key="volunteering" data-section-id="volunteering" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Volunteering</Typography>
              <Divider sx={dividerSx} />
              {savedVolunteering.map((vol, i) => (
                <Box key={i} sx={{ mb: itemMb, ...overflowFix }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: `calc(${fontSize} * 1.1)`, fontFamily: font, fontStyle: fontStyle, ...overflowFix }}>
                    {vol.subtopic}
                  </Typography>
                  <Typography variant="caption" sx={getTextStyle()}>{vol.fromDate} - {vol.toDate}</Typography>
                  <Typography variant="body2" sx={{ mt: 1, ...globalFontStyle, whiteSpace: "pre-line" }}>{vol.content}</Typography>
                </Box>
              ))}
            </div>
          ) : null;

        case 'interests':
          return savedInterests.length > 0 ? (
            <div key="interests" data-section-id="interests" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Interests</Typography>
              <Divider sx={dividerSx} />
              <List dense disablePadding>
                {savedInterests.map((interest, i) => (
                  <ListItem key={i} sx={{ p: 0, mb: itemMb }}>
                    <ListItemText primary={`• ${interest}`}
                      sx={{ '& .MuiTypography-root': { fontSize, fontFamily: font, fontStyle, lineHeight: `${lineSpacing}px`, ...overflowFix } }} />
                  </ListItem>
                ))}
              </List>
            </div>
          ) : null;

        case 'websites':
          return savedWebsites.length > 0 ? (
            <div key="websites" data-section-id="websites" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Websites / Profiles</Typography>
              <Divider sx={dividerSx} />
              {savedWebsites.map((site, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle }}>
                  <a href={site.url.startsWith('http') ? site.url : `https://${site.url}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {site.name || site.url}
                  </a>
                </Typography>
              ))}
            </div>
          ) : null;

        case 'additionalInfo':
          return savedAdditionalInfo.length > 0 ? (
            <div key="additionalInfo" data-section-id="additionalInfo" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Additional Information</Typography>
              <Divider sx={dividerSx} />
              {savedAdditionalInfo.map((info, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle }}>{info}</Typography>
              ))}
            </div>
          ) : null;

        case 'references':
          return savedReferences.length > 0 ? (
            <div key="references" data-section-id="references" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>References</Typography>
              <Divider sx={dividerSx} />
              {savedReferences.map((ref, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle, whiteSpace: "pre-line" }}>{ref}</Typography>
              ))}
            </div>
          ) : null;

        case 'softwareSkills':
          return softwareSkills.length > 0 ? (
            <div key="softwareSkills" data-section-id="softwareSkills" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Software Skills</Typography>
              <Divider sx={dividerSx} />
              {softwareSkills.map((skill, i) => (
                <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle }}>
                  {skill.name} — {skill.level}%
                </Typography>
              ))}
            </div>
          ) : null;

        // Left sections moved to right side
        case 'contact':
          return (
            <div key="contact" data-section-id="contact" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Contact</Typography>
              <Divider sx={dividerSx} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...overflowFix }}>
                {data.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <PhoneIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                  <Typography variant="body2" sx={getTextStyle()}>{data.phone}</Typography>
                </Box>}
                {data.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <EmailIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                  <Typography variant="body2" sx={getTextStyle()}>{data.email}</Typography>
                </Box>}
                {data.city && <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ...overflowFix }}>
                  <LocationOnIcon sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={getTextStyle()}>{data.city}</Typography>
                </Box>}
                {data.website && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <LanguageIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                  <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {data.website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </Box>}
                {data.linkedIn && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <LinkedInIcon sx={{ fontSize: 16, color: '#0077b5', flexShrink: 0 }} />
                  <a href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: '#0077b5', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                  </a>
                </Box>}
                {data.drivingLicence && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DriveEtaIcon sx={{ fontSize: 16, color: '#4caf50', flexShrink: 0 }} />
                  <Typography variant="body2" sx={getTextStyle()}>DL: {data.drivingLicence}</Typography>
                </Box>}
              </Box>
            </div>
          );

        case 'aboutMe':
          return (
            <div key="aboutMe" data-section-id="aboutMe" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>About Me</Typography>
              <Divider sx={dividerSx} />
              <Typography variant="body2" sx={{ textIndent: `${paragraphIndent}px`, ...globalFontStyle }}>
                {savedSummary || data.profile}
              </Typography>
            </div>
          );

        case 'skills':
          return (
            <div key="skills" data-section-id="skills" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Skills</Typography>
              <Divider sx={dividerSx} />
              <List dense disablePadding>
                {(savedSkills.length > 0 ? savedSkills.map(s => s.name) : [
                  "Creative writing & solutions", "Copywriting", "Communication",
                  "Strategic planning", "Project coordination", "Storytelling", "Presentation skills"
                ]).map((skill, i) => (
                  <ListItem key={i} sx={{ p: 0, mb: itemMb }}>
                    <ListItemText primary={skill}
                      sx={{ '& .MuiTypography-root': { fontSize, fontFamily: font, fontStyle, lineHeight: `${lineSpacing}px`, ...overflowFix } }} />
                  </ListItem>
                ))}
              </List>
            </div>
          );

        case 'education':
          return educationEntries.length > 0 ? (
            <div key="education" data-section-id="education" className="resume-section" style={sectionStyle}>
              <Typography variant="h6" sx={sectionHeadingSx}>Education</Typography>
              <Divider sx={dividerSx} />
              {educationEntries.map((edu, i) => (
                <Box key={i} sx={{ mb: itemMb, ...overflowFix }}>
                  {edu.educationLevel === "school" ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: "bold", ...globalFontStyle }}>{edu.standard} Standard</Typography>
                      <Typography variant="body2" sx={getTextStyle()}>{edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ''}{edu.district ? `, ${edu.district}` : ''}</Typography>
                      {edu.stream && <Typography variant="body2" sx={getTextStyle()}>Stream: {edu.stream}</Typography>}
                      {edu.percentage && <Typography variant="body2" sx={getTextStyle()}>Percentage: {edu.percentage}%</Typography>}
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: "bold", ...globalFontStyle }}>
                        {edu.degreeName} {edu.programType && `(${edu.programType})`}
                      </Typography>
                      <Typography variant="body2" sx={getTextStyle()}>{edu.collegeName}{edu.collegeLocation ? `, ${edu.collegeLocation}` : ''}</Typography>
                      {edu.specialization && <Typography variant="body2" sx={getTextStyle()}>Specialization: {edu.specialization}</Typography>}
                      {(edu.cgpa || edu.percentage || edu.grade) && (
                        <Typography variant="body2" sx={getTextStyle()}>
                          {edu.cgpa && `CGPA: ${edu.cgpa}`}{edu.percentage && ` | Percentage: ${edu.percentage}%`}{edu.grade && ` | Grade: ${edu.grade}`}
                        </Typography>
                      )}
                      {edu.gradMonth && edu.gradYear && (
                        <Typography variant="body2" sx={{ fontStyle: "italic", ...globalFontStyle }}>
                          Graduated: {edu.gradMonth} {edu.gradYear}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </div>
          ) : null;

        default:
          if (sectionId && sectionId.startsWith('custom_')) {
            const customSectionName = sectionId.replace('custom_', '');
            const items = (customSections[customSectionName] || []).filter(i => i && i.trim() !== '');
            if (items.length > 0) {
              return (
                <div key={sectionId} data-section-id={sectionId} className="resume-section" style={sectionStyle}>
                  <Typography variant="h6" sx={sectionHeadingSx}>
                    {customSectionName.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  <Divider sx={dividerSx} />
                  {items.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: itemMb, ...globalFontStyle, whiteSpace: "pre-line" }}>
                      • {item}
                    </Typography>
                  ))}
                </div>
              );
            }
            return null;
          }
          return null;
      }
    }).filter(Boolean);
  };

  // ─── Left Sidebar Live Renderer (for measurement ref) ────────────────────
  // Uses className="sidebar-section" so paginateSidebarContent can measure heights
  const renderLeftSidebarSections = () => {
    const sectionStyle = { ...overflowFix, marginBottom: `${sectionSpacing}px` };
    const itemMb = `${paragraphSpacing / 8}px`;

    const sidebarText = (extra = {}) => ({
      fontFamily: font, fontSize: fontSize, fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`, color: 'white', ...overflowFix, ...extra,
    });

    const sidebarHeading = {
      fontWeight: "bold", mb: 1, fontSize: headingSize, lineHeight: 1.3,
      fontFamily: font, fontStyle: fontStyle, color: 'white', ...overflowFix,
    };

    return leftSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'contact':
          return (
            <div key="contact" className="sidebar-section" style={sectionStyle}>
              <Typography variant="h6" sx={sidebarHeading}>Contact</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...overflowFix }}>
                {data.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>{data.phone}</Typography>
                </Box>}
                {data.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <EmailIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>{data.email}</Typography>
                </Box>}
                {data.city && <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ...overflowFix }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: 'white', mt: '2px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>{data.city}</Typography>
                </Box>}
                {data.website && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <LanguageIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'white', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {data.website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </Box>}
                {data.linkedIn && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <LinkedInIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <a href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'white', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                  </a>
                </Box>}
                {data.drivingLicence && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DriveEtaIcon sx={{ fontSize: 16, color: '#4caf50', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>DL: {data.drivingLicence}</Typography>
                </Box>}
              </Box>
            </div>
          );

        case 'aboutMe':
          return (
            <div key="aboutMe" className="sidebar-section" style={sectionStyle}>
              <Typography variant="h6" sx={sidebarHeading}>About Me</Typography>
              <Typography variant="body2" sx={sidebarText({ textIndent: `${paragraphIndent}px` })}>
                {savedSummary || data.profile}
              </Typography>
            </div>
          );

        case 'skills':
          return (
            <div key="skills" className="sidebar-section" style={sectionStyle}>
              <Typography variant="h6" sx={sidebarHeading}>Skills</Typography>
              <List dense disablePadding>
                {(savedSkills.length > 0 ? savedSkills.map(s => s.name) : [
                  "Creative writing & solutions", "Copywriting", "Communication",
                  "Strategic planning", "Project coordination", "Storytelling", "Presentation skills"
                ]).map((skill, i) => (
                  <ListItem key={i} sx={{ p: 0, mb: itemMb }}>
                    <ListItemText primary={skill}
                      sx={{ '& .MuiTypography-root': { fontSize, fontFamily: font, fontStyle, color: 'white', lineHeight: `${lineSpacing}px`, ...overflowFix } }} />
                  </ListItem>
                ))}
              </List>
            </div>
          );

        case 'education':
          return educationEntries.length > 0 ? (
            <div key="education" className="sidebar-section" style={sectionStyle}>
              <Typography variant="h6" sx={sidebarHeading}>Education</Typography>
              {educationEntries.map((edu, i) => (
                <Box key={i} sx={{ mb: itemMb, ...overflowFix }}>
                  {edu.educationLevel === "school" ? (
                    <>
                      <Typography variant="body2" sx={sidebarText({ fontWeight: "bold" })}>{edu.standard} Standard</Typography>
                      <Typography variant="body2" sx={sidebarText()}>{edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ''}{edu.district ? `, ${edu.district}` : ''}</Typography>
                      {edu.stream && <Typography variant="body2" sx={sidebarText()}>Stream: {edu.stream}</Typography>}
                      {edu.percentage && <Typography variant="body2" sx={sidebarText()}>Percentage: {edu.percentage}%</Typography>}
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={sidebarText({ fontWeight: "bold" })}>
                        {edu.degreeName} {edu.programType && `(${edu.programType})`}
                      </Typography>
                      <Typography variant="body2" sx={sidebarText()}>{edu.collegeName}{edu.collegeLocation ? `, ${edu.collegeLocation}` : ''}</Typography>
                      {edu.specialization && <Typography variant="body2" sx={sidebarText()}>Specialization: {edu.specialization}</Typography>}
                      {(edu.cgpa || edu.percentage || edu.grade) && (
                        <Typography variant="body2" sx={sidebarText()}>
                          {edu.cgpa && `CGPA: ${edu.cgpa}`}{edu.percentage && ` | ${edu.percentage}%`}{edu.grade && ` | Grade: ${edu.grade}`}
                        </Typography>
                      )}
                      {edu.gradMonth && edu.gradYear && (
                        <Typography variant="body2" sx={sidebarText({ fontStyle: 'italic' })}>
                          Graduated: {edu.gradMonth} {edu.gradYear}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </div>
          ) : null;

        default:
          if (sectionId.startsWith('custom_')) {
            const customName = sectionId.replace('custom_', '');
            const items = (customSections[customName] || []).filter(i => i && i.trim() !== '');
            return items.length > 0 ? (
              <div key={sectionId} className="sidebar-section" style={sectionStyle}>
                <Typography variant="h6" sx={sidebarHeading}>{customName.replace(/_/g, ' ').toUpperCase()}</Typography>
                <List dense disablePadding>
                  {items.map((item, i) => (
                    <ListItem key={i} sx={{ p: 0, mb: itemMb }}>
                      <ListItemText primary={item}
                        sx={{ '& .MuiTypography-root': { fontSize, fontFamily: font, fontStyle, color: 'white', lineHeight: `${lineSpacing}px`, ...overflowFix } }} />
                    </ListItem>
                  ))}
                </List>
              </div>
            ) : null;
          }
          // Right sections moved to sidebar — show condensed version
          return (
            <div key={sectionId} className="sidebar-section" style={sectionStyle}>
              <Typography variant="h6" sx={sidebarHeading}>{getSectionTitle(sectionId)}</Typography>
              {renderRightSectionInSidebar(sectionId)}
            </div>
          );
      }
    }).filter(Boolean);
  };

  // ─── Right sections condensed in sidebar ─────────────────────────────────
  const renderRightSectionInSidebar = (sectionId) => {
    const textSx = { fontFamily: font, fontSize, fontStyle, color: 'white', lineHeight: `${lineSpacing}px`, ...overflowFix };
    switch (sectionId) {
      case 'experience':
        return workExperiences.slice(0, 2).map((work, i) => (
          <Box key={i} sx={{ mb: 1, ...overflowFix }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", ...textSx }}>{work.jobTitle}</Typography>
            <Typography variant="caption" sx={{ ...textSx, color: 'rgba(255,255,255,0.8)' }}>
              {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
            </Typography>
          </Box>
        ));
      case 'languages':
        return savedLanguages.map((lang, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>{lang.name} {lang.level ? `– ${lang.level}` : ""}</Typography>
        ));
      case 'certifications':
        return savedCertifications.map((cert, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>{cert.name}</Typography>
        ));
      case 'accomplishments':
        return savedAccomplishments.slice(0, 3).map((acc, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>• {acc.substring(0, 60)}</Typography>
        ));
      case 'interests':
        return savedInterests.map((interest, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>• {interest}</Typography>
        ));
      case 'websites':
        return savedWebsites.map((site, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>
            <a href={site.url.startsWith('http') ? site.url : `https://${site.url}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: 'white', textDecoration: 'none', wordBreak: 'break-all' }}>
              {site.name || site.url}
            </a>
          </Typography>
        ));
      case 'additionalInfo':
        return savedAdditionalInfo.map((info, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>• {info}</Typography>
        ));
      case 'references':
        return savedReferences.slice(0, 2).map((ref, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>{ref.substring(0, 60)}</Typography>
        ));
      case 'softwareSkills':
        return softwareSkills.map((skill, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 0.5, ...textSx }}>{skill.name} — {skill.level}%</Typography>
        ));
      case 'volunteering':
        return savedVolunteering.slice(0, 2).map((vol, i) => (
          <Box key={i} sx={{ mb: 1, ...overflowFix }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", ...textSx }}>{vol.subtopic}</Typography>
            <Typography variant="caption" sx={{ ...textSx, color: 'rgba(255,255,255,0.8)' }}>{vol.fromDate} - {vol.toDate}</Typography>
          </Box>
        ));
      default:
        return null;
    }
  };

  // ─── Left sidebar rendered for display (uses outerHTML from sidebarPages) ─
  // This is the original renderLeftSidebar kept intact for page 0 avatar display logic
  const renderLeftSidebar = (pageIndex) => {
    const sidebarText = (extra = {}) => ({
      fontFamily: font, fontSize: fontSize, fontStyle: fontStyle,
      lineHeight: `${lineSpacing}px`, color: 'white', ...overflowFix, ...extra,
    });

    const sidebarHeading = {
      fontWeight: "bold", mb: 1, fontSize: headingSize, lineHeight: 1.3,
      fontFamily: font, fontStyle: fontStyle, color: 'white', ...overflowFix,
    };

    return leftSectionOrder.map(sectionId => {
      if (pageIndex !== 0) return null; // sidebar only on page 1

      switch (sectionId) {
        case 'contact':
          return (
            <Box key="contact" sx={{ width: "100%", mb: `${sectionSpacing}px`, ...overflowFix }}>
              <Typography variant="h6" sx={sidebarHeading}>Contact</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...overflowFix }}>
                {data.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>{data.phone}</Typography>
                </Box>}
                {data.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <EmailIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>{data.email}</Typography>
                </Box>}
                {data.city && <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ...overflowFix }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: 'white', mt: '2px', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>{data.city}</Typography>
                </Box>}
                {data.website && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <LanguageIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'white', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {data.website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </Box>}
                {data.linkedIn && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...overflowFix }}>
                  <LinkedInIcon sx={{ fontSize: 16, color: 'white', flexShrink: 0 }} />
                  <a href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: 'white', textDecoration: 'none', fontFamily: font, fontSize: fontSize, wordBreak: 'break-all' }}>
                    {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                  </a>
                </Box>}
                {data.drivingLicence && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DriveEtaIcon sx={{ fontSize: 16, color: '#4caf50', flexShrink: 0 }} />
                  <Typography variant="body2" sx={sidebarText()}>DL: {data.drivingLicence}</Typography>
                </Box>}
              </Box>
            </Box>
          );

        case 'aboutMe':
          return (
            <Box key="aboutMe" sx={{ width: "100%", mb: `${sectionSpacing}px`, ...overflowFix }}>
              <Typography variant="h6" sx={sidebarHeading}>About Me</Typography>
              <Typography variant="body2" sx={sidebarText({ textIndent: `${paragraphIndent}px` })}>
                {savedSummary || data.profile}
              </Typography>
            </Box>
          );

        case 'skills':
          return (
            <Box key="skills" sx={{ width: "100%", mb: `${sectionSpacing}px`, ...overflowFix }}>
              <Typography variant="h6" sx={sidebarHeading}>Skills</Typography>
              <List dense disablePadding>
                {(savedSkills.length > 0 ? savedSkills.map(s => s.name) : [
                  "Creative writing & solutions", "Copywriting", "Communication",
                  "Strategic planning", "Project coordination", "Storytelling", "Presentation skills"
                ]).map((skill, i) => (
                  <ListItem key={i} sx={{ p: 0, mb: `${paragraphSpacing / 8}px` }}>
                    <ListItemText primary={skill}
                      sx={{ '& .MuiTypography-root': { fontSize, fontFamily: font, fontStyle, color: 'white', lineHeight: `${lineSpacing}px`, ...overflowFix } }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );

        case 'education':
          return educationEntries.length > 0 ? (
            <Box key="education" sx={{ width: "100%", mb: `${sectionSpacing}px`, ...overflowFix }}>
              <Typography variant="h6" sx={sidebarHeading}>Education</Typography>
              {educationEntries.map((edu, i) => (
                <Box key={i} sx={{ mb: `${paragraphSpacing / 8}px`, ...overflowFix }}>
                  {edu.educationLevel === "school" ? (
                    <>
                      <Typography variant="body2" sx={sidebarText({ fontWeight: "bold" })}>{edu.standard} Standard</Typography>
                      <Typography variant="body2" sx={sidebarText()}>{edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ''}{edu.district ? `, ${edu.district}` : ''}</Typography>
                      {edu.stream && <Typography variant="body2" sx={sidebarText()}>Stream: {edu.stream}</Typography>}
                      {edu.percentage && <Typography variant="body2" sx={sidebarText()}>Percentage: {edu.percentage}%</Typography>}
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={sidebarText({ fontWeight: "bold" })}>
                        {edu.degreeName} {edu.programType && `(${edu.programType})`}
                      </Typography>
                      <Typography variant="body2" sx={sidebarText()}>{edu.collegeName}{edu.collegeLocation ? `, ${edu.collegeLocation}` : ''}</Typography>
                      {edu.specialization && <Typography variant="body2" sx={sidebarText()}>Specialization: {edu.specialization}</Typography>}
                      {(edu.cgpa || edu.percentage || edu.grade) && (
                        <Typography variant="body2" sx={sidebarText()}>
                          {edu.cgpa && `CGPA: ${edu.cgpa}`}{edu.percentage && ` | ${edu.percentage}%`}{edu.grade && ` | Grade: ${edu.grade}`}
                        </Typography>
                      )}
                      {edu.gradMonth && edu.gradYear && (
                        <Typography variant="body2" sx={sidebarText({ fontStyle: 'italic' })}>
                          Graduated: {edu.gradMonth} {edu.gradYear}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ) : null;

        default:
          if (sectionId.startsWith('custom_')) {
            const customName = sectionId.replace('custom_', '');
            const items = (customSections[customName] || []).filter(i => i && i.trim() !== '');
            return items.length > 0 ? (
              <Box key={sectionId} sx={{ width: "100%", mb: `${sectionSpacing}px`, ...overflowFix }}>
                <Typography variant="h6" sx={sidebarHeading}>{customName.replace(/_/g, ' ').toUpperCase()}</Typography>
                <List dense disablePadding>
                  {items.map((item, i) => (
                    <ListItem key={i} sx={{ p: 0, mb: `${paragraphSpacing / 8}px` }}>
                      <ListItemText primary={item}
                        sx={{ '& .MuiTypography-root': { fontSize, fontFamily: font, fontStyle, color: 'white', lineHeight: `${lineSpacing}px`, ...overflowFix } }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null;
          }
          return (
            <Box key={sectionId} sx={{ width: "100%", mb: `${sectionSpacing}px`, ...overflowFix }}>
              <Typography variant="h6" sx={sidebarHeading}>{getSectionTitle(sectionId)}</Typography>
              {renderRightSectionInSidebar(sectionId)}
            </Box>
          );
      }
    }).filter(Boolean);
  };

  // ─── Resume30-style: Pagination for sidebar content ───────────────────────
  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;

    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    // Account for avatar/photo area on page 0 (approx 180px) + top/bottom margin
    const avatarHeight = 180;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - avatarHeight;

    const sections = Array.from(sidebarContentRef.current.querySelectorAll('.sidebar-section'));

    let newSidebarPages = [];
    let currentPageSections = [];
    let currentHeight = 0;

    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;

      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) {
          newSidebarPages.push([...currentPageSections]);
        }
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }

      if (index === sections.length - 1 && currentPageSections.length > 0) {
        newSidebarPages.push([...currentPageSections]);
      }
    });

    if (newSidebarPages.length === 0) {
      newSidebarPages.push([]);
    }

    setSidebarPages(newSidebarPages);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing,
      lineSpacing, fontSize, headingSize, font, primaryColor, customSections]);

  // ─── Resume30-style: Pagination for right content ────────────────────────
  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;

    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    // Account for name/title header on page 0 (approx 80px) + top/bottom margin
    const headerHeight = 80;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;

    const sections = Array.from(rightContentRef.current.querySelectorAll('.resume-section'));

    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;

    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;

      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) {
          newPages.push([...currentPageSections]);
        }
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }

      if (index === sections.length - 1 && currentPageSections.length > 0) {
        newPages.push([...currentPageSections]);
      }
    });

    if (newPages.length === 0) {
      newPages.push([]);
    }

    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing,
      lineSpacing, fontSize, headingSize, font, primaryColor]);

  // ─── Trigger both pagination functions (Resume30 pattern) ────────────────
  useEffect(() => {
    const id = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(id);
  }, [
    paginateSidebarContent,
    paginateMainContent,
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests, savedWebsites,
    customSections, leftSectionOrder, rightSectionOrder,
    fontSize, headingSize, font, fontStyle, lineSpacing,
    paragraphSpacing, sectionSpacing, lineWeight, sideMargins, topBottomMargin,
  ]);

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", position: "relative", m: 5 }}>
      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage >= totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ── Hidden measurement boxes (Resume30 pattern) ── */}
      {/* Left sidebar measurement ref */}
      <Box ref={sidebarContentRef} sx={{
        visibility: 'hidden', position: 'absolute', top: -9999,
        width: '25%', overflow: 'hidden', ...globalFontStyle
      }}>
        <Box sx={{ p: `${topBottomMargin}px ${sideMargins}px`, overflow: 'hidden' }}>
          {renderLeftSidebarSections()}
        </Box>
      </Box>

      {/* Right content measurement ref */}
      <Box ref={rightContentRef} sx={{
        visibility: 'hidden', position: 'absolute', top: -9999,
        width: '75%', overflow: 'hidden', ...globalFontStyle
      }}>
        <Box sx={{ p: `${topBottomMargin}px ${sideMargins}px`, overflow: 'hidden' }}>
          {renderRightSections()}
        </Box>
      </Box>

      {/* Old mainContentRef kept for any external usage (hidden, empty) */}
      <Box ref={mainContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -9999 }} />

      {/* Resume Pages */}
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              margin: `${topBottomMargin}px auto`,
              boxShadow: pageIndex === currentPage ? 3 : 0,
              borderRadius: "16px",
              overflow: "hidden",
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              fontFamily: font, fontSize, fontStyle, lineHeight: `${lineSpacing}px`,
              boxSizing: 'border-box',
              '@media print': { display: 'flex', boxShadow: 'none', borderRadius: 0, margin: '0 auto' }
            }}
          >
            {/* Left Sidebar */}
            <Box sx={{
              width: "25%",
              maxWidth: "25%",
              flexShrink: 0,
              bgcolor: primaryColor,
              color: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 3,
              overflow: 'hidden',
              boxSizing: 'border-box',
              fontFamily: font, fontSize, fontStyle,
            }}>
              {/* Avatar only on page 0 */}
              {pageIndex === 0 && (
                <Box sx={{
                  width: "100%",
                  bgcolor: primaryColor,
                  clipPath: "polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: sectionSpacing / 10,
                  pt: 4,
                  overflow: 'hidden',
                }}>
                  <Avatar src={photo || "https://via.placeholder.com/150"}
                    sx={{ width: 140, height: 140, border: `${lineWeight}px solid white`, boxShadow: 3 }} />
                </Box>
              )}

              {/* ── Resume30-style: render sidebar from sidebarPages outerHTML ── */}
              {sidebarPages[pageIndex] ? (
                <Box sx={{ width: '100%', ...globalFontStyle }}
                  dangerouslySetInnerHTML={{ __html: sidebarPages[pageIndex].join('') }}
                />
              ) : (
                // Fallback for page 0 before pagination runs
                pageIndex === 0 ? (
                  <Box sx={{ width: '100%' }}>
                    {renderLeftSidebar(pageIndex)}
                  </Box>
                ) : null
              )}
            </Box>

            {/* Right Content */}
            <Box sx={{
              flex: 1,
              minWidth: 0,
              bgcolor: "#f8f2ee",
              p: `${sideMargins / 10}px`,
              margin: `${topBottomMargin / 10}px 0`,
              overflow: 'hidden',
              boxSizing: 'border-box',
              fontFamily: font, fontSize, fontStyle,
            }}>
              {/* Name & title only on page 0 */}
              {pageIndex === 0 && (
                <Box mb={sectionSpacing / 5} sx={overflowFix}>
                  <Typography variant="h4" sx={{
                    fontWeight: "bold",
                    fontSize: `calc(${headingSize} * 1.5)`,
                    fontFamily: font, fontStyle, ...overflowFix,
                  }}>
                    {data.firstName.toUpperCase()} {data.lastName.toUpperCase()}
                  </Typography>
                  <Typography variant="subtitle1" sx={{
                    color: primaryColor,
                    fontSize: `calc(${fontSize} * 1.2)`,
                    fontFamily: font, fontStyle, ...overflowFix,
                  }}>
                    {data.currentPosition}
                  </Typography>
                </Box>
              )}

              {/* ── Resume30-style: render right content from pages outerHTML ── */}
              {pages[pageIndex] ? (
                <Box sx={{ overflow: 'hidden', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%', ...globalFontStyle }}
                  dangerouslySetInnerHTML={{ __html: pages[pageIndex].join('') }}
                />
              ) : (
                // Fallback before pagination runs
                <Box sx={{ overflow: 'hidden', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                  {renderRightSections(null)}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder them within each side or move sections between left sidebar and right content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  columns: 2,
  hasPhoto: true,
  hasHeadshot: true,
};

export default Resume9;